import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle2, Calendar, Clock, Users, MessageSquare, AlertCircle, Info, Loader2 } from 'lucide-react';
import { CampaignData } from '@/hooks/sms/campaign/types';
import { cn } from '@/lib/utils';
import { getSmsPricing } from '@/services/smsApi';
import { OPT_OUT_MESSAGE } from '@/utils/smsUtils';

interface CampaignReviewProps {
  campaignData: CampaignData;
  audienceSize: number;
  isSubmitting: boolean;
  onSaveCampaign: () => Promise<boolean>;
  userRestaurants: any[];
  disabledRestaurants?: any[];
}

const CampaignReview: React.FC<CampaignReviewProps> = ({
  campaignData,
  audienceSize,
  isSubmitting,
  onSaveCampaign,
  userRestaurants,
  disabledRestaurants = []
}) => {
  // State for SMS pricing
  const [pricingData, setPricingData] = useState({
    pricePerSegment: 0.15,
    currency: 'NZD',
    isLoading: true
  });

  // Calculate the full message length including opt-out message
  const messageOnlyLength = campaignData.message.length;
  const totalMessageLength = messageOnlyLength + OPT_OUT_MESSAGE.length;
  
  // Calculate estimated segments based on total length including opt-out message
  const hasUnicode = Array.from(campaignData.message + OPT_OUT_MESSAGE).some(char => char.charCodeAt(0) > 127);
  const charLimit = hasUnicode ? 70 : 160;
  const segments = Math.ceil(totalMessageLength / charLimit) || 1;
  
  // Calculate cost using dynamic pricing
  const totalSegments = segments * audienceSize;
  const estimatedCost = (totalSegments * pricingData.pricePerSegment).toFixed(2);

  // Fetch pricing data when component mounts or restaurant changes
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        // Use first restaurant ID in the list, or default if using all
        const restaurantId = campaignData.selectedRestaurants.includes('all') 
          ? (userRestaurants[0]?.id || '') 
          : campaignData.selectedRestaurants[0] || '';
        
        if (!restaurantId) {
          console.warn('No restaurant ID available for pricing');
          return;
        }
        
        const pricing = await getSmsPricing(restaurantId);
        
        setPricingData({
          pricePerSegment: pricing.price_per_segment,
          currency: pricing.currency,
          isLoading: false
        });
        
        console.log('Loaded SMS pricing:', pricing);
      } catch (error) {
        console.error('Failed to load SMS pricing:', error);
        setPricingData(current => ({
          ...current,
          isLoading: false
        }));
      }
    };
    
    fetchPricing();
  }, [campaignData.selectedRestaurants, userRestaurants]);

  // Format currency symbol
  const getCurrencySymbol = (currency: string) => {
    switch(currency) {
      case 'NZD': return '$';
      case 'AUD': return '$';
      case 'GBP': return '£';
      case 'EUR': return '€';
      default: return currency;
    }
  };
  
  // Format scheduled time for display
  const getFormattedScheduleTime = () => {
    if (campaignData.scheduleType === 'now') {
      return 'Send immediately';
    } else if (campaignData.scheduleDate) {
      const date = campaignData.scheduleDate.toLocaleDateString('en-NZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      return `${date} at ${campaignData.scheduleTime}`;
    }
    return 'Not scheduled';
  };
  
  // Get restaurant names
  const getRestaurantNames = () => {
    if (campaignData.selectedRestaurants.includes('all')) {
      return 'All Restaurants';
    }
    
    return userRestaurants
      .filter(r => campaignData.selectedRestaurants.includes(r.id))
      .map(r => r.name)
      .join(', ');
  };
  
  // Detect potential issues
  const issues = [];
  
  if (audienceSize > 1000) {
    issues.push({
      type: 'warning',
      icon: <AlertTriangle className="h-4 w-4" />,
      title: 'Large Audience',
      description: `You're sending to ${audienceSize.toLocaleString()} recipients. Consider testing with a smaller segment first.`
    });
  }
  
  if (segments > 1) {
    issues.push({
      type: 'info',
      icon: <Info className="h-4 w-4" />,
      title: 'Multiple SMS Segments',
      description: `Your message uses ${segments} SMS segments (${totalMessageLength} total characters including opt-out message). This may increase costs.`
    });
  }
  
  if (hasUnicode) {
    issues.push({
      type: 'info',
      icon: <Info className="h-4 w-4" />,
      title: 'Special Characters Detected',
      description: 'Your message contains special characters that reduce the character limit to 70 per segment.'
    });
  }
  
  if (disabledRestaurants.length > 0) {
    issues.push({
      type: 'warning',
      icon: <AlertCircle className="h-4 w-4" />,
      title: 'SMS Not Enabled',
      description: `SMS is not enabled for ${disabledRestaurants.length} selected restaurant(s). Enable SMS before sending.`
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Review & Save</CardTitle>
        <CardDescription>
          Review your campaign details and save when ready
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-xl font-bold mb-1">{campaignData.name}</h3>
          {campaignData.description && (
            <p className="text-muted-foreground">{campaignData.description}</p>
          )}
        </div>
        
        <Separator />
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">
                SCHEDULE
              </h4>
              <div className="flex items-center space-x-2">
                {campaignData.scheduleType === 'now' ? (
                  <Badge>Send Immediately</Badge>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="outline">{getFormattedScheduleTime()}</Badge>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">
                AUDIENCE
              </h4>
              <div className="space-y-2">
                <Badge variant="outline">
                  {audienceSize.toLocaleString()} recipients
                </Badge>
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">
                    {campaignData.audienceType === 'all' ? 'All customers' : 'Filtered audience'}
                  </span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-2">
                RESTAURANTS
              </h4>
              <Badge variant="outline">
                {getRestaurantNames()}
              </Badge>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-2">
              CONTENT
            </h4>
            <div>
              <p className="whitespace-pre-wrap break-words border border-border rounded-md p-3 bg-muted/50">
                {campaignData.message}
                <span className="text-xs text-muted-foreground block mt-2">
                  {OPT_OUT_MESSAGE.trim()}
                </span>
              </p>
              {campaignData.usePersonalization && (
                <Badge variant="outline" className="mt-2">
                  Personalisation Enabled
                </Badge>
              )}
            </div>
          </div>
          
          <Card className="bg-muted/30">
            <CardContent className="pt-6 space-y-4">
              <h4 className="font-medium">Campaign Summary</h4>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{audienceSize.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">Recipients</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{totalMessageLength}</div>
                  <div className="text-xs text-muted-foreground">Characters</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">{segments * audienceSize}</div>
                  <div className="text-xs text-muted-foreground">SMS Segments</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {pricingData.isLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    ) : (
                      `${getCurrencySymbol(pricingData.currency)}${estimatedCost}`
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">Estimated Cost</div>
                </div>
              </div>
              
              {audienceSize > 0 && (
                <Button 
                  className="w-full mt-4 gap-2"
                  onClick={onSaveCampaign}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving Campaign...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      Save Campaign
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
          
          {issues.length > 0 && (
            <div className="space-y-3">
              {issues.map((issue, index) => (
                <Alert 
                  key={index} 
                  variant={
                    issue.type === 'warning' ? 'destructive' : 'default'
                  }
                  className={cn(
                    issue.type === 'info' && 'bg-blue-50 text-blue-800 border-blue-200',
                    issue.type === 'warning' && 'bg-amber-50 text-amber-800 border-amber-200'
                  )}
                >
                  {issue.icon}
                  <AlertTitle>{issue.title}</AlertTitle>
                  <AlertDescription>
                    {issue.description}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CampaignReview;
